import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { RangeSlider } from "./Ranges";
import { RANGE_LIMITS } from "./filterOptions";
import '../search/search.css';

const stopPropagation = (e) => e.stopPropagation();

const RANGES = {
    released: { label: 'RELEASED', format: (r) => `${r.min} – ${r.max}` },
    runtime: { label: 'RUNTIME', format: (r) => `${r.min} – ${r.max} min` },
    imdb: { label: 'IMDB', format: (r) => `${r.min} – ${r.max}` },
    rottentomatoes: { label: 'ROTTEN TOMATOES', format: (r) => `${r.min} – ${r.max}` },
};

const MOBILE_TABS = [
    ['search', 'SEARCH'], ['genres', 'GENRE'], ['languages', 'LANGUAGE'],
    ['ratings', 'RATINGS'], ['released', 'RELEASED'],
];

// Chip sections never grow past this many rows; the rest slides in on further pages
const CHIP_ROWS = 2;

const checkedCount = (items) => items.filter((i) => i.checked).length;

const countLabel = (items) => {
    const n = checkedCount(items);
    return n ? `${n} OF ${items.length}` : 'ANY';
};

function ArrowIcon() {
    return (
        <svg className="kf-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
            <path d="M4 12h15M13 6l6 6-6 6" />
        </svg>
    );
}

function NavArrow({ back = false }) {
    return (
        <svg className="kf-nav-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
            {back ? <path d="M20 12H5M11 6l-6 6 6 6" /> : <path d="M4 12h15M13 6l6 6-6 6" />}
        </svg>
    );
}

function Chip({ label, on = false, onClick }) {
    return (
        <button
            type="button"
            className={`kf-chip${on ? ' is-on' : ''}`}
            aria-pressed={on}
            onClick={onClick}
        >
            {label}
        </button>
    );
}

// "+N MORE →" at the end of a page, "← BACK" at the start of the next one
function NavChip({ label, back = false, onClick }) {
    return (
        <button type="button" className="kf-chip kf-chip-nav" onClick={onClick}>
            {back ? <NavArrow back /> : null}
            <span>{label}</span>
            {back ? null : <NavArrow />}
        </button>
    );
}

function SectionHeader({ title, items }) {
    return (
        <div className="kf-head">
            <span className="kf-label">{title}</span>
            <span className="kf-hint">{countLabel(items)}</span>
        </div>
    );
}

// Rows a run of chips takes up once wrapped into `width`
function rowsNeeded(widths, width, gap) {
    let rows = 1;
    let x = -gap;
    for (const w of widths) {
        if (x + gap + w <= width) {
            x += gap + w;
        } else {
            rows += 1;
            x = w;
        }
    }
    return rows;
}

// Splits chips into pages of at most `rows` rows. Every page but the last ends with a
// MORE chip and every page but the first starts with a BACK chip, so those are packed too.
function packPages(widths, { width, gap, rows, moreWidth, backWidth }) {
    const n = widths.length;
    const fits = (seq) => rowsNeeded(seq, width, gap) <= rows;
    const pages = [];
    let i = 0;
    while (i < n) {
        const lead = pages.length ? [backWidth] : [];
        if (fits([...lead, ...widths.slice(i)])) {
            pages.push([i, n]);
            break;
        }
        let count = Math.max(1, n - i - 1);
        while (count > 1 && !fits([...lead, ...widths.slice(i, i + count), moreWidth])) count -= 1;
        pages.push([i, i + count]);
        i += count;
    }
    return pages;
}

// Free-width chips capped at CHIP_ROWS rows. Chips that don't fit go on further pages
// that slide in from the right, the way the design canvas did it. Chip widths are
// measured from a hidden copy so the split follows the real font and viewport.
function PagedChipGroup({ items, id, toggle, rows = CHIP_ROWS }) {
    const viewportRef = useRef(null);
    const measureRef = useRef(null);
    const [layout, setLayout] = useState(null);
    const [page, setPage] = useState(0);

    const relayout = useCallback(() => {
        const viewport = viewportRef.current;
        const measure = measureRef.current;
        if (!viewport || !measure) return;
        const widths = Array.from(measure.children, (el) => el.getBoundingClientRect().width);
        const backWidth = widths.pop();
        const moreWidth = widths.pop();
        const style = getComputedStyle(measure);
        const gap = parseFloat(style.columnGap) || 0;
        const rowGap = parseFloat(style.rowGap) || 0;
        const chipHeight = measure.firstElementChild ? measure.firstElementChild.getBoundingClientRect().height : 0;
        const width = viewport.clientWidth;
        const pages = packPages(widths, { width, gap, rows, moreWidth, backWidth });
        const shownRows = pages.length > 1 ? rows : Math.min(rows, rowsNeeded(widths, width, gap));
        setLayout({ pages, height: shownRows * chipHeight + (shownRows - 1) * rowGap });
    }, [rows]);

    useLayoutEffect(relayout, [relayout, items.length]);
    useEffect(() => {
        window.addEventListener('resize', relayout);
        return () => window.removeEventListener('resize', relayout);
    }, [relayout]);

    const pages = layout ? layout.pages : [[0, items.length]];
    const current = Math.min(page, pages.length - 1);
    const pageWidth = 100 / pages.length;
    const chip = (item) => (
        <Chip key={item.value} label={item.value} on={item.checked} onClick={() => toggle(id, item.value)} />
    );
    const onCount = (from, to) => checkedCount(items.slice(from, to));

    return (
        <div className="kf-pager" ref={viewportRef} style={{ height: layout ? layout.height : undefined }}>
            <div
                className="kf-pager-track"
                style={{ width: `${pages.length * 100}%`, transform: `translateX(-${current * pageWidth}%)` }}
            >
                {pages.map(([from, to], p) => {
                    const before = onCount(0, from);
                    const after = onCount(to, items.length);
                    return (
                        <div className="kf-chips" key={p} style={{ flex: `0 0 ${pageWidth}%` }} aria-hidden={p !== current}>
                            {p > 0 ? (
                                <NavChip back label={`BACK${before ? ` · ${before} ON` : ''}`} onClick={() => setPage(p - 1)} />
                            ) : null}
                            {items.slice(from, to).map(chip)}
                            {to < items.length ? (
                                <NavChip label={`+${items.length - to} MORE${after ? ` · ${after} ON` : ''}`} onClick={() => setPage(p + 1)} />
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {/* Hidden copy for measuring; the last two are the widest nav chips can get */}
            <div className="kf-chips kf-chips-measure" ref={measureRef} aria-hidden="true">
                {items.map(chip)}
                <NavChip label={`+${items.length} MORE · ${items.length} ON`} />
                <NavChip back label={`BACK · ${items.length} ON`} />
            </div>
        </div>
    );
}

function Range({ id, filters, updateRange }) {
    const limits = RANGE_LIMITS[id];
    const range = filters[id];
    return (
        <div className="kf-range">
            <div className="kf-range-head">
                <span className="kf-label">{RANGES[id].label}</span>
                <span className="kf-range-value">{RANGES[id].format(range)}</span>
            </div>
            <div className="kf-track">
                <RangeSlider start={range.min} end={range.max} min={limits.min} max={limits.max}
                             step={limits.step} rangeType={id} range={range}
                             updateRange={updateRange} showLabel={false} />
            </div>
        </div>
    );
}

function Describe({ query, setQuery, onSubmit }) {
    return (
        <div className="kf-describe" data-tour="describe">
            <div className="kf-head">
                <label htmlFor="kf-query" className="kf-label">DESCRIBE A FILM</label>
                <span className="kf-hint">OPTIONAL</span>
            </div>
            <div className="kf-input-row">
                <input
                    id="kf-query"
                    type="text"
                    className="kf-input"
                    value={query}
                    maxLength={300}
                    placeholder="a christmas movie, or something edgy and arty…"
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                />
                {query ? (
                    <button type="button" className="kf-clear" onClick={() => setQuery('')}>CLEAR</button>
                ) : null}
            </div>
        </div>
    );
}

function Actions({ onReset, onShow, busy }) {
    return (
        <div className="kf-buttons">
            <button type="button" className="kf-btn" onClick={onReset} disabled={busy}>RESET</button>
            <button type="button" className="kf-btn is-primary" onClick={onShow} disabled={busy} aria-busy={busy}>
                SHOW FILMS {busy ? <span className="kf-spinner" aria-label="Searching" /> : <ArrowIcon />}
            </button>
        </div>
    );
}

export default function Filters({ filters, toggleCheckbox, updateRange, searchQuery, onShowFilms, onReset, busy = false }) {
    const { isLandscape } = useMobileOrientation();
    const isSmallLandscape = isMobile && isLandscape;
    const [query, setQuery] = useState(searchQuery || '');
    const [tab, setTab] = useState('search');
    // Bumped on reset so the chip pagers go back to their first page
    const [resetKey, setResetKey] = useState(0);

    const show = () => {
        if (!busy) onShowFilms(query);
    };
    const reset = () => {
        setQuery('');
        setResetKey((k) => k + 1);
        onReset();
    };

    const describe = <Describe query={query} setQuery={setQuery} onSubmit={show} />;
    const range = (id) => <Range key={id} id={id} filters={filters} updateRange={updateRange} />;
    const chips = (id) => (
        <PagedChipGroup key={`${id}-${resetKey}`} items={filters[id]} id={id} toggle={toggleCheckbox} />
    );

    if (isSmallLandscape) {
        return (
            <div className="kfm" onClick={stopPropagation} onMouseDown={stopPropagation}>
                <div className="kfm-tabs">
                    {MOBILE_TABS.map(([id, label]) => {
                        const n = Array.isArray(filters[id]) ? checkedCount(filters[id]) : 0;
                        return (
                            <button key={id} type="button" aria-pressed={tab === id}
                                    className={`kfm-tab${tab === id ? ' is-on' : ''}`} onClick={() => setTab(id)}>
                                {label}{n ? ` · ${n}` : ''}
                            </button>
                        );
                    })}
                </div>
                <div className="kfm-body">
                    {tab === 'search' ? describe : null}
                    {tab === 'genres' ? <><SectionHeader title="GENRE" items={filters.genres} />{chips('genres')}</> : null}
                    {tab === 'languages' ? <><SectionHeader title="LANGUAGE" items={filters.languages} />{chips('languages')}</> : null}
                    {tab === 'ratings' ? <div className="kf-range-list">{range('imdb')}{range('rottentomatoes')}</div> : null}
                    {tab === 'released' ? <div className="kf-range-list">{range('released')}{range('runtime')}</div> : null}
                </div>
                <Actions onReset={reset} onShow={show} busy={busy} />
            </div>
        );
    }

    return (
        <div className="kf" onClick={stopPropagation} onMouseDown={stopPropagation}>
            {/* Scrolls on short screens */}
            <div className="kf-scroll">
                <div className="kf-gap-top" />
                {describe}
                <div className="kf-gap" />
                <div className="kf-grid kf-body">
                    <div className="kf-sections">
                        <section data-tour="genres"><SectionHeader title="GENRE" items={filters.genres} />{chips('genres')}</section>
                        <section data-tour="languages"><SectionHeader title="LANGUAGE" items={filters.languages} />{chips('languages')}</section>
                        <Actions onReset={reset} onShow={show} busy={busy} />
                    </div>
                    <div className="kf-ranges" data-tour="ranges">
                        {['released', 'runtime', 'imdb', 'rottentomatoes'].map(range)}
                    </div>
                </div>
            </div>
        </div>
    );
}
